/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: inline-block;
}

.qds-button:disabled,
[aria-disabled='true'] {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

.qds-action {
  transform: scaleX(var(--qds-direction-factor, 1));
}

.qds-container {
  display: grid;
  grid-template-areas: 'button';
}

.qds-button {
  align-items: center;
  appearance: none;
  background-color: initial;
  border: none;
  border-radius: var(
      --qds-control-border-radius-top-left,
      var(--qds-control-border-radius)
    )
    var(--qds-control-border-radius-top-right, var(--qds-control-border-radius))
    var(
      --qds-control-border-radius-bottom-right,
      var(--qds-control-border-radius)
    )
    var(
      --qds-control-border-radius-bottom-left,
      var(--qds-control-border-radius)
    );
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  grid-area: button;
  justify-content: center;
  outline: none;
  padding-block: 0;
  print-color-adjust: exact;
  user-select: none;
  white-space: var(--qds-internal-white-space-property, nowrap);
  width: 100%;

  &:any-link {
    text-decoration: none;
  }
}

.qds-focus-ring {
  border-radius: max(
      1px,
      var(
        --qds-control-border-radius-top-left,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-top-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-left,
        var(--qds-control-border-radius)
      )
    );
  grid-area: button;
  isolation: isolate;
  outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
  outline-offset: var(--qds-focus-border-offset);
  pointer-events: none;
  visibility: hidden;
  width: calc(
    100% + var(--qds-control-border-width) -
      var(
        --qds-control-border-width-inline-end,
        var(--qds-control-border-width)
      )
  );
  height: calc(
    100% + var(--qds-control-border-width) -
      var(--qds-control-border-width-block-end, var(--qds-control-border-width))
  );
}

.qds-badge {
  border-radius: var(--qds-control-rounded-border-radius);
  font: var(--qds-badge-text);
  height: var(--qds-badge-height);
  padding-inline: var(--qds-badge-padding-horizontal);
}

.qds-icon {
  flex-shrink: 0;
}

[data-size='small'] {
  font: var(--qds-control-small-text);

  &.qds-button {
    gap: var(--qds-control-small-gap-internal);
    height: var(--qds-control-small-height);
    padding-inline: var(--qds-control-button-small-padding-horizontal);

    &.qds-icon-only {
      padding-inline: 0;
      width: var(--qds-control-small-height);
    }
  }

  &.qds-icon {
    font-size: var(--qds-control-small-icon-size);
  }
}

[data-size='standard'] {
  font: var(--qds-control-standard-text);

  &.qds-button {
    gap: var(--qds-control-standard-gap-internal);
    height: var(--qds-control-standard-height);
    padding-inline: var(--qds-control-button-standard-padding-horizontal);

    &.qds-icon-only {
      padding-inline: 0;
      width: var(--qds-control-standard-height);
    }
  }

  &.qds-icon {
    font-size: var(--qds-control-standard-icon-size);
  }
}

[data-size='large'] {
  font: var(--qds-control-large-text);

  &.qds-button {
    gap: var(--qds-control-large-gap-internal);
    height: var(--qds-control-large-height);
    padding-inline: var(--qds-control-button-large-padding-horizontal);

    &.qds-icon-only {
      padding-inline: 0;
      width: var(--qds-control-large-height);
    }
  }

  &.qds-icon {
    font-size: var(--qds-control-large-icon-size);
  }
}

[data-importance='subdued'] {
  color: var(--qds-theme-link-standard-default);

  &.qds-badge {
    background-color: var(--qds-theme-feedback-message-informational);
    color: var(--qds-theme-feedback-message-informational-contrast);
  }

  &.qds-button {
    border-block-end: var(--qds-control-border-width) solid transparent;
    padding-inline: 0;

    &:hover {
      border-block-end: var(--qds-control-border-width) solid
        var(--qds-theme-link-standard-hover);
      color: var(--qds-theme-link-standard-hover);

      .qds-badge {
        background-color: var(--qds-theme-link-standard-hover);
      }
    }

    &:active {
      border-block-end: var(--qds-control-border-width) solid
        var(--qds-theme-link-standard-pressed);
      color: var(--qds-theme-link-standard-pressed);

      .qds-badge {
        background-color: var(--qds-theme-link-standard-pressed);
      }
    }

    &:not(.qds-icon-only) {
      border-radius: 0;
    }

    &.qds-icon-only {
      border: none;
      color: var(--qds-theme-control-text-standard);

      &:hover {
        background-color: var(--qds-theme-interactive-background-hover);
      }

      &:active {
        background-color: var(--qds-theme-interactive-background-pressed);
      }
    }
  }
}

[data-importance='standard'] {
  color: var(--qds-theme-control-text-standard);

  &.qds-badge {
    background-color: var(--qds-theme-feedback-message-neutral);
    color: var(--qds-theme-feedback-message-neutral-contrast);
  }

  &.qds-button {
    border-color: var(--qds-theme-control-border);
    border-style: solid;
    border-width: var(--qds-control-border-width)
      var(
        --qds-control-border-width-inline-end,
        var(--qds-control-border-width)
      )
      var(--qds-control-border-width-block-end, var(--qds-control-border-width))
      var(--qds-control-border-width);

    &:hover {
      background-color: var(--qds-theme-interactive-background-hover);

      .qds-badge {
        color: var(--qds-theme-interactive-background-hover);
      }
    }

    &:active {
      background-color: var(--qds-theme-interactive-background-pressed);

      .qds-badge {
        color: var(--qds-theme-interactive-background-pressed);
      }
    }
  }
}

[data-importance='emphasized'] {
  &.qds-badge {
    background-color: var(--qds-theme-signature-color-contrast);
    color: var(--qds-theme-signature-color-default);
  }

  &.qds-button {
    background-color: var(--qds-theme-signature-color-default);
    color: var(--qds-theme-signature-color-contrast);

    &:hover {
      background-color: var(--qds-theme-signature-color-hover);

      .qds-badge {
        color: var(--qds-theme-signature-color-hover);
      }
    }

    &:active {
      background-color: var(--qds-theme-signature-color-pressed);

      .qds-badge {
        color: var(--qds-theme-signature-color-pressed);
      }
    }
  }
}

[data-variant='destructive'] {
  &.qds-badge {
    background-color: var(--qds-theme-feedback-action-destructive-contrast);
    color: var(--qds-theme-feedback-action-destructive-default);
  }

  &.qds-button {
    background-color: var(--qds-theme-feedback-action-destructive-default);
    color: var(--qds-theme-feedback-action-destructive-contrast);

    &:hover {
      background-color: var(--qds-theme-feedback-action-destructive-hover);

      .qds-badge {
        color: var(--qds-theme-feedback-action-destructive-hover);
      }
    }

    &:active {
      background-color: var(--qds-theme-feedback-action-destructive-pressed);

      .qds-badge {
        color: var(--qds-theme-feedback-action-destructive-pressed);
      }
    }
  }
}

[data-variant='hero'] {
  &.qds-button {
    border-radius: var(
        --qds-control-border-radius-top-left,
        var(--qds-control-hero-border-radius)
      )
      var(
        --qds-control-border-radius-top-right,
        var(--qds-control-hero-border-radius)
      )
      var(
        --qds-control-border-radius-bottom-right,
        var(--qds-control-hero-border-radius)
      )
      var(
        --qds-control-border-radius-bottom-left,
        var(--qds-control-hero-border-radius)
      );
    font: var(--qds-control-hero-text);
    gap: var(--qds-control-hero-gap-internal);
    padding-block: var(--qds-control-hero-padding-auto-height);
    white-space: normal;

    &[data-importance='standard'] {
      padding-block: calc(
          var(--qds-control-hero-padding-auto-height) -
            var(
              --qds-control-border-width-block-start,
              var(--qds-control-border-width)
            )
        )
        calc(
          var(--qds-control-hero-padding-auto-height) -
            var(
              --qds-control-border-width-block-end,
              var(--qds-control-border-width)
            )
        );
    }

    &:not([data-importance='subdued']) {
      padding-inline: var(--qds-control-button-hero-padding-horizontal);
    }

    &.qds-icon-only {
      height: var(--qds-control-hero-height);
      padding: 0;
      width: var(--qds-control-hero-height);
    }
  }

  &.qds-focus-ring {
    border-radius: max(
        1px,
        var(
          --qds-control-border-radius-top-left,
          var(--qds-control-hero-border-radius)
        )
      )
      max(
        1px,
        var(
          --qds-control-border-radius-top-right,
          var(--qds-control-hero-border-radius)
        )
      )
      max(
        1px,
        var(
          --qds-control-border-radius-bottom-right,
          var(--qds-control-hero-border-radius)
        )
      )
      max(
        1px,
        var(
          --qds-control-border-radius-bottom-left,
          var(--qds-control-hero-border-radius)
        )
      );
  }

  &.qds-icon {
    font-size: var(--qds-control-hero-icon-size);
  }
}

.qds-button:focus-visible ~ .qds-focus-ring {
  visibility: visible;
}

[data-action^='dropdown'] {
  &.qds-button {
    padding-inline: var(--qds-control-input-padding-horizontal);
    justify-content: space-between;
  }
}

[data-action^='dropdown'][data-importance='standard'] {
  &.qds-button {
    background-color: var(--qds-theme-control-input-background);
  }
}
